import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bug, ExternalLink, MessageSquareHeart, Send, Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';

const PLAY_STORE_REVIEW_URL = process.env.REACT_APP_GOOGLE_PLAY_REVIEW_URL || 'https://play.google.com/store/apps';

function FeedbackSupportTab({ t, user }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: 'BUG',
    message: '',
    rating: 5,
    contact_email: user?.email || '',
    screenshot_data: '',
  });

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/feedback/mine');
      setSubmissions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, []);

  const handleScreenshotChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setFormData((prev) => ({ ...prev, screenshot_data: '' }));
      return;
    }

    if (file.size > 1_500_000) {
      toast.error(t('feedback.errors.imageTooLarge'));
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, screenshot_data: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      await axios.post('/feedback', {
        ...formData,
        platform: 'web',
        rating: formData.type === 'REVIEW' ? Number(formData.rating) : null,
      });
      toast.success(t('feedback.success.sent'));
      setFormData((prev) => ({
        ...prev,
        message: '',
        screenshot_data: '',
      }));
      await loadFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('feedback.errors.submit'));
    } finally {
      setSubmitting(false);
    }
  };

  const openPlayStore = () => {
    window.open(PLAY_STORE_REVIEW_URL, '_blank', 'noopener,noreferrer');
  };

  const renderStars = (rating) => (
    <div className="flex items-center gap-1 text-amber-500">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star key={`star-${index}`} className={`h-4 w-4 ${index < Number(rating || 0) ? 'fill-current' : ''}`} />
      ))}
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <Card className="border-rose-100 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Bug className="h-5 w-5 text-rose-600" />
            {t('feedback.title')}
          </CardTitle>
          <CardDescription>{t('feedback.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('feedback.form.type')}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUG">{t('feedback.types.bug')}</SelectItem>
                    <SelectItem value="SUPPORT">{t('feedback.types.support')}</SelectItem>
                    <SelectItem value="REVIEW">{t('feedback.types.review')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('feedback.form.contact')}</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(event) => setFormData((prev) => ({ ...prev, contact_email: event.target.value }))}
                  placeholder="email@smartinventory.app"
                />
              </div>
            </div>

            {formData.type === 'REVIEW' && (
              <div className="space-y-2">
                <Label>{t('feedback.form.rating')}</Label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={`rating-${rating}`}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, rating }))}
                      className={`rounded-full border px-3 py-2 text-sm transition ${formData.rating === rating ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600'}`}
                    >
                      {rating} ★
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('feedback.form.message')}</Label>
              <Textarea
                value={formData.message}
                onChange={(event) => setFormData((prev) => ({ ...prev, message: event.target.value }))}
                placeholder={t('feedback.form.messagePlaceholder')}
                className="min-h-[150px]"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('feedback.form.screenshot')}</Label>
              <Input type="file" accept="image/*" onChange={handleScreenshotChange} />
              <p className="text-xs text-gray-500">{t('feedback.form.screenshotHint')}</p>
              {formData.screenshot_data && (
                <img
                  src={formData.screenshot_data}
                  alt="Screenshot preview"
                  className="max-h-48 rounded-xl border border-gray-200 object-cover"
                />
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={submitting} className="bg-rose-600 text-white hover:bg-rose-700">
                <Send className="mr-2 h-4 w-4" />
                {submitting ? t('feedback.form.sending') : t('feedback.form.send')}
              </Button>
              <Button type="button" variant="outline" onClick={openPlayStore}>
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('feedback.playStoreButton')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-6">
        <Card className="border-amber-100 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <MessageSquareHeart className="h-5 w-5 text-amber-600" />
              {t('feedback.communityTitle')}
            </CardTitle>
            <CardDescription>{t('feedback.communityDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-700">
            <p>{t('feedback.communityText')}</p>
            <div className="rounded-2xl bg-amber-50 p-4 text-amber-900">
              <p className="font-medium">{t('feedback.reviewPromptTitle')}</p>
              <p className="mt-1 text-sm">{t('feedback.reviewPromptText')}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-100 shadow-lg">
          <CardHeader>
            <CardTitle>{t('feedback.recentTitle')}</CardTitle>
            <CardDescription>{t('feedback.recentDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-sm text-gray-500">{t('common.loading')}</p>
            ) : submissions.length === 0 ? (
              <p className="text-sm text-gray-500">{t('feedback.empty')}</p>
            ) : (
              submissions.map((submission) => (
                <div key={submission.id} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge variant="outline">{t(`feedback.types.${String(submission.type || '').toLowerCase()}`)}</Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(submission.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  {submission.rating ? <div className="mt-2">{renderStars(submission.rating)}</div> : null}
                  <p className="mt-2 text-sm text-gray-700">{submission.message}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FeedbackSupportTab;